import React, { useMemo, useState } from 'react';
import { Modal, TreeSelect, Spin, Typography, Divider, Button } from 'antd';
import type { ModalProps } from 'antd';
import { useVwTkfWbsHierarchy, useGetPrimaveraTasksSmall } from '../../hooks/genericHooks';
import type { VwTkfWbsHierarchy } from '../../types';

interface WbsSelectModalProps {
  open: boolean;
  onOk: (selected: VwTkfWbsHierarchy & { children?: VwTkfWbsHierarchy[] }) => void;
  onCancel: () => void;
  filters?: { [key: string]: any };
  modalProps?: Partial<ModalProps>;
}

function buildTree(flat: VwTkfWbsHierarchy[], tasks: any[]): any[] {
  const idMap: Record<string, any> = {};
  const roots: any[] = [];
  // Build a quick map of activities by wbs_id (string)
  const taskMap: Record<string, any[]> = {};
  (tasks || []).forEach((t: any) => {
    const wbsId = t?.wbs_id != null ? String(t.wbs_id) : '';
    if (!wbsId) return;
    if (!taskMap[wbsId]) taskMap[wbsId] = [];
    taskMap[wbsId].push(t);
  });
  flat.forEach(item => {
    const wbsKey = item.wbs_id.toString();
    const isLeaf = !item.has_children;
    // Allow selection if this node itself has activities, regardless of leaf status
    const hasActivities = (taskMap[wbsKey]?.length > 0);
    idMap[item.wbs_id.toString()] = {
      title: (
        <span style={hasActivities ? { color: '#1890ff' } : {}}>
          {item.wbs_name}
          {hasActivities && <span style={{ marginLeft: 4 }}>★</span>}
        </span>
      ),
      value: item.wbs_id.toString(),
      key: item.wbs_id.toString(),
      selectable: hasActivities,
      item,
      children: [],
      has_children: item.has_children,
      has_activities: hasActivities
    };
  });
  flat.forEach(item => {
    if (item.parent_wbs_id && idMap[item.parent_wbs_id.toString()]) {
      idMap[item.parent_wbs_id.toString()].children.push(idMap[item.wbs_id.toString()]);
    } else {
      roots.push(idMap[item.wbs_id.toString()]);
    }
  });
  Object.values(idMap).forEach((node: any) => {
    if (!node.children.length) delete node.children;
  });
  return roots;
}

const findNodeByKey = (nodes: any[], key: string | number): any | null => {
  for (const node of nodes) {
    if (node.key === key) return node;
    if (node.children) {
      const found = findNodeByKey(node.children, key);
      if (found) return found;
    }
  }
  return null;
};

// Compute all ancestor keys for a set of matched node keys
const getAncestorKeys = (treeData: any[], matchKeys: (string | number)[]): (string | number)[] => {
  const keyMap: Record<string | number, any> = {};
  function buildMap(nodes: any[], parent?: string | number) {
    nodes.forEach(node => {
      keyMap[node.key] = { ...node, parent };
      if (node.children) buildMap(node.children, node.key);
    });
  }
  buildMap(treeData);
  const ancestors = new Set<string | number>();
  matchKeys.forEach(key => {
    let current = keyMap[key];
    while (current && current.parent !== undefined) {
      ancestors.add(current.parent);
      current = keyMap[current.parent];
    }
  });
  return Array.from(ancestors);
};

const WbsSelectModal: React.FC<WbsSelectModalProps> = ({ open, onOk, onCancel, filters, modalProps }) => {
  const { data: wbsData, isLoading: isLoadingWbs } = useVwTkfWbsHierarchy();
  const { data: allTasks, isLoading: isLoadingTasks } = useGetPrimaveraTasksSmall();
  const [selectedWbsId, setSelectedWbsId] = useState<string | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<(string | number)[]>([]);
  const [searchValue, setSearchValue] = useState('');

  const filteredData = useMemo(() => {
    let items = wbsData?.data || [];
    if (filters) {
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') {
          items = items.filter((item: any) => item[k] === v);
        }
      });
    }
    return items;
  }, [wbsData, filters]);

  const treeData = useMemo(() => {
    if (!filteredData.length) return [];
    const tasks = allTasks && Array.isArray(allTasks.data) ? allTasks.data : [];
    return buildTree(filteredData, tasks);
  }, [filteredData, allTasks]);

  const handleOk = () => {
    if (!selectedWbsId) return;
    const node = findNodeByKey(treeData, selectedWbsId);
    if (node && node.item && node.has_activities) {
      // Attach children if any
      onOk({ ...node.item, children: node.children || [] });
    }
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
    if (!value) {
      setExpandedKeys([]);
      return;
    }
    const matchKeys: (string | number)[] = [];
    const tokens = value
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean);
    function traverse(nodes: any[]) {
      nodes.forEach(node => {
        // Extract text label from title (string or JSX)
        let label = '';
        if (typeof node.title === 'string') {
          label = node.title;
        } else if (
          node.title &&
          typeof node.title === 'object' &&
          'props' in node.title &&
          node.title.props &&
          node.title.props.children
        ) {
          const children = node.title.props.children;
          if (typeof children === 'string') label = children;
          else if (Array.isArray(children)) label = children.map((c: any) => (typeof c === 'string' ? c : '')).join(' ');
          else if (typeof children === 'number') label = children.toString();
        }
        const idStr = String(node.item?.wbs_id ?? '');
        const nameStr = String(node.item?.wbs_name ?? '');
        const haystack = `${label} ${idStr} ${nameStr}`.toLowerCase();
        const isMatch = tokens.every(t => haystack.includes(t));
        if (isMatch) {
          matchKeys.push(node.key);
        }
        if (node.children) traverse(node.children);
      });
    }
    traverse(treeData);
    const expanded = [...matchKeys, ...getAncestorKeys(treeData, matchKeys)];
    setExpandedKeys(Array.from(new Set(expanded)));
  };

  return (
    <Modal
      {...modalProps}
      open={open}
      onOk={handleOk}
      onCancel={onCancel}
      okButtonProps={{
        disabled:
          !selectedWbsId ||
          !(findNodeByKey(treeData, selectedWbsId)?.has_activities)
      }}
      title="Select WBS Item"
      width={600}
      destroyOnClose
    >
      <Typography.Text type="secondary" style={{ marginBottom: 16, display: 'block' }}>
        Please select a WBS node that has activities (blue/star = selectable).
      </Typography.Text>
      <Divider style={{ margin: '12px 0' }} />
      <Spin spinning={isLoadingWbs || isLoadingTasks}>
        <TreeSelect
          style={{ width: '100%' }}
          value={selectedWbsId}
          dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
          treeData={treeData}
          placeholder="Type to search or expand nodes"
          treeDefaultExpandAll={false}
          showSearch
          onSearch={handleSearch}
          searchValue={searchValue}
          virtual={true}
          filterTreeNode={(input, node) => {
            let label = '';
            if (typeof node.title === 'string') {
              label = node.title;
            } else if (
              node.title &&
              typeof node.title === 'object' &&
              'props' in node.title &&
              (node.title as any).props &&
              (node.title as any).props.children
            ) {
              const children = (node.title as any).props.children;
              if (typeof children === 'string') label = children;
              else if (Array.isArray(children)) label = children.map((c: any) => (typeof c === 'string' ? c : '')).join(' ');
              else if (typeof children === 'number') label = children.toString();
            }
            const idStr = String((node as any).item?.wbs_id ?? '');
            const nameStr = String((node as any).item?.wbs_name ?? '');
            const haystack = `${label} ${idStr} ${nameStr}`.toLowerCase();
            const tokens = input
              .toLowerCase()
              .split(/\s+/)
              .filter(Boolean);
            return tokens.every(t => haystack.includes(t));
          }}
          onChange={val => setSelectedWbsId(val as string)}
          treeLine
          allowClear
          treeNodeFilterProp="title"
          treeExpandedKeys={expandedKeys}
          onTreeExpand={setExpandedKeys}
        />
      </Spin>
    </Modal>
  );
};

export default WbsSelectModal;
